import { humanize } from "./format";

/**
 * Purchasing domain helpers.
 *
 * Mirrors the supplier MOQ / order-multiple invariant enforced authoritatively
 * in `create_purchase_order` (see supabase/migrations). The browser uses these
 * to give the operator immediate, specific feedback before submit — the trusted
 * RPC remains the single source of truth and will still reject an invalid line.
 */

export interface SupplierOrderRule {
  minOrderQty: number | null;
  orderMultiple: number | null;
}

export interface QuantityValidation {
  valid: boolean;
  reason?: string;
}

/**
 * Validate a line quantity against a supplier's minimum order quantity and
 * order multiple. A MOQ/multiple of 0 or 1 means "no constraint".
 */
export function validateOrderQuantity(qty: number, rule: SupplierOrderRule): QuantityValidation {
  const moq = rule.minOrderQty ?? 0;
  if (moq > 1 && qty < moq) {
    return { valid: false, reason: `Quantity ${qty} is below the minimum order quantity of ${moq}.` };
  }
  const multiple = rule.orderMultiple ?? 0;
  if (multiple > 1 && qty % multiple !== 0) {
    return { valid: false, reason: `Quantity ${qty} must be a multiple of ${multiple}.` };
  }
  return { valid: true };
}

/**
 * Resolve a purchase order's transaction currency, mirroring the authoritative
 * resolution in `create_purchase_order`: explicit → supplier → organisation.
 * There is deliberately no silent "USD" fallback — a monetary supplier cost
 * must never change currency meaning because a value was absent.
 */
export function resolvePoCurrency(
  explicit: string | null | undefined,
  supplierCurrency: string | null | undefined,
  orgCurrency: string,
): string {
  const c = (explicit ?? "").trim() || (supplierCurrency ?? "").trim() || orgCurrency;
  return c.toUpperCase();
}

/**
 * Whether a monetary amount denominated in `amountCurrency` would silently
 * change meaning if placed on a document in `documentCurrency`. Empty/unknown
 * amount currencies are treated as "no declared denomination" (not a mismatch).
 */
export function currenciesDiffer(
  amountCurrency: string | null | undefined,
  documentCurrency: string,
): boolean {
  const a = (amountCurrency ?? "").trim().toUpperCase();
  const b = (documentCurrency ?? "").trim().toUpperCase();
  return !!a && !!b && a !== b;
}

/**
 * Canonical purchase-order status labels + presentation gates + progress.
 *
 * These MIRROR the lifecycle enforced server-side inside the RPCs
 * (create/approve/receive/cancel) — see migrations 0007, 0062, 0095-0098.
 * They are presentation-only: they never create new lifecycle authority.
 *
 * The authoritative lifecycle is:
 *   draft ──approve──▶ pending ──receive──▶ partial ──receive──▶ received
 *   (draft | pending | partial ──cancel──▶ cancelled)
 *
 * `closed` exists in the enum but is NOT reachable through any current RPC
 * transition; it is labelled readably here but its lifecycle is a separate
 * backend decision (out of scope for this presentation work).
 */

export const PO_STATUS = {
  DRAFT: "draft",
  PENDING: "pending",
  PARTIAL: "partial",
  RECEIVED: "received",
  CLOSED: "closed",
  CANCELLED: "cancelled",
} as const;

export type PoStatus = (typeof PO_STATUS)[keyof typeof PO_STATUS];

/** Statuses in which a PO may still receive goods. */
export const RECEIVABLE_STATUSES: string[] = [PO_STATUS.PENDING, PO_STATUS.PARTIAL];

/** Statuses in which a PO may be "marked as ordered" (draft → pending). */
export const APPROVABLE_STATUSES: string[] = [PO_STATUS.DRAFT];

/** Statuses in which a PO may still be cancelled. */
export const CANCELLABLE_STATUSES: string[] = [PO_STATUS.DRAFT, PO_STATUS.PENDING, PO_STATUS.PARTIAL];

export function canApprovePo(status: string | null | undefined): boolean {
  return APPROVABLE_STATUSES.includes(status ?? "");
}

export function canReceivePo(status: string | null | undefined): boolean {
  return RECEIVABLE_STATUSES.includes(status ?? "");
}

export function canCancelPo(status: string | null | undefined): boolean {
  return CANCELLABLE_STATUSES.includes(status ?? "");
}

/**
 * Operator-facing label for a purchase-order status. Never leaks a raw enum.
 * "pending" is labelled "Ordered" (the PO has been marked ordered, awaiting
 * receipt) — honest wording that matches the "Mark as ordered" action.
 */
export function poStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case PO_STATUS.DRAFT: return "Draft";
    case PO_STATUS.PENDING: return "Ordered";
    case PO_STATUS.PARTIAL: return "Partially received";
    case PO_STATUS.RECEIVED: return "Received";
    case PO_STATUS.CLOSED: return "Closed";
    case PO_STATUS.CANCELLED: return "Cancelled";
    default: return humanize(status) || "Unknown";
  }
}

export interface PoLineProgressInput {
  qty_ordered: number;
  qty_received: number;
}

export interface PoProgress {
  ordered: number;
  received: number;
  outstanding: number;
  nothingReceived: boolean;
  fullyReceived: boolean;
}

/**
 * Units-based receiving progress for a purchase order, derived from its line
 * quantities. Units are the single unit of measure (never mixed with line
 * count or monetary value). Outstanding is clamped at zero so an over-received
 * line never produces a negative remainder.
 */
export function computePoProgress(items: PoLineProgressInput[]): PoProgress {
  const ordered = items.reduce((s, it) => s + (Number(it.qty_ordered) || 0), 0);
  const received = items.reduce((s, it) => s + (Number(it.qty_received) || 0), 0);
  const outstanding = Math.max(ordered - received, 0);
  return {
    ordered,
    received,
    outstanding,
    nothingReceived: received === 0,
    fullyReceived: ordered > 0 && received >= ordered,
  };
}